/**
 * Shared utility constants and functions for the MD3 Expressive Shape Engine.
 * Ported from androidx.graphics.shapes.Utils.kt
 *
 * @see https://m3.material.io/styles/shape/overview-principles
 */

/** Epsilon for floating-point distance comparisons */
export const DISTANCE_EPSILON = 1e-4;

/** Epsilon for angle comparisons */
export const ANGLE_EPSILON = 1e-4;

/** Pi as a float constant */
export const FLOAT_PI = Math.PI;

/**
 * Calculates the Euclidean distance between two coordinate offsets.
 * @param dx - Difference in x coordinates
 * @param dy - Difference in y coordinates
 * @returns The distance
 */
export function distance(dx: number, dy: number): number {
	return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculates the squared Euclidean distance (cheaper than distance).
 * @param dx - Difference in x coordinates
 * @param dy - Difference in y coordinates
 * @returns The squared distance
 */
export function distanceSquared(dx: number, dy: number): number {
	return dx * dx + dy * dy;
}

/**
 * Converts polar coordinates to Cartesian (x, y).
 * @param radius - The radial distance
 * @param angle - The angle in radians
 * @returns A tuple [x, y]
 */
export function radialToCartesian(
	radius: number,
	angle: number,
): [number, number] {
	return [radius * Math.cos(angle), radius * Math.sin(angle)];
}

/**
 * Returns value modulo divisor, always positive.
 * @param value - The value
 * @param divisor - The divisor
 * @returns Positive modulo result
 */
export function positiveModulo(value: number, divisor: number): number {
	return ((value % divisor) + divisor) % divisor;
}

/**
 * Linearly interpolates between start and stop.
 * @param start - Start value (at fraction=0)
 * @param stop - End value (at fraction=1)
 * @param fraction - Interpolation factor [0,1]
 * @returns Interpolated value
 */
export function interpolate(
	start: number,
	stop: number,
	fraction: number,
): number {
	return start + (stop - start) * fraction;
}

/**
 * Returns the square of a value.
 * @param value - The value to square
 */
export function square(value: number): number {
	return value * value;
}

/**
 * Returns a unit direction vector from (0,0) toward (dx, dy).
 * @param dx - X component
 * @param dy - Y component
 * @returns [nx, ny] normalized direction
 */
export function directionVector(dx: number, dy: number): [number, number] {
	const d = distance(dx, dy);
	if (d < DISTANCE_EPSILON) return [0, 0];
	return [dx / d, dy / d];
}

/**
 * Checks if the turn from prev→curr→next is convex (clockwise in screen coords).
 * @param prevX - Previous vertex x
 * @param prevY - Previous vertex y
 * @param currX - Current vertex x
 * @param currY - Current vertex y
 * @param nextX - Next vertex x
 * @param nextY - Next vertex y
 * @returns true if convex
 */
export function convex(
	prevX: number,
	prevY: number,
	currX: number,
	currY: number,
	nextX: number,
	nextY: number,
): boolean {
	// Cross product of (curr-prev) × (next-curr)
	const v1x = currX - prevX;
	const v1y = currY - prevY;
	const v2x = nextX - currX;
	const v2y = nextY - currY;
	return v1x * v2y - v1y * v2x > 0;
}

/**
 * Type for a function that transforms a point (x, y) → (x', y').
 */
export type PointTransformer = (x: number, y: number) => [number, number];
